import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchProgram } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";

const formatDate = (iso) => {
  if (!iso) return "";
  return iso.substring(0, 10).replace(/-/g, "/");
};

export default function BuildProgramGoalAchievementListPage() {
  const { businessDomainId, businessUnitId, programId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [rows, setRows] = useState([]);
  const [programName, setProgramName] = useState("");
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const program = await fetchProgram(programId);
      setProgramName(program.programName || "");

      const goals = program.goals || [];
      const achievements = program.achievements || [];

      // 目標ごとに最新の実績を紐付け
      const combined = goals.map((goal, idx) => {
        const indicatorName = goal.indicator?.indicatorName || "";
        const matched = achievements
          .filter((a) => a.goal?.indicator?.indicatorName === indicatorName)
          .sort((a, b) => {
            if (!a.actualDate && !b.actualDate) return 0;
            if (!a.actualDate) return 1;
            if (!b.actualDate) return -1;
            return new Date(b.actualDate) - new Date(a.actualDate);
          });
        const latest = matched[0] || null;
        return {
          id: idx,
          indicatorName,
          unit: goal.indicator?.unit || "",
          plannedValue: goal.plannedValue ?? "",
          plannedDate: goal.plannedDate || "",
          deadline: goal.deadline || "",
          actualValue: latest?.actualValue ?? "",
          actualDate: latest?.actualDate || "",
          achievementCount: matched.length,
        };
      });

      setRows(combined);
    } catch (e) {
      notify.apiError(e);
    } finally {
      setLoading(false);
    }
  }, [programId]);

  useEffect(() => { loadData(); }, [loadData]);

  const columns = [
    { field: "indicatorName", headerName: t("program.goal.indicator"), flex: 1, minWidth: 160 },
    { field: "unit",          headerName: t("program.goal.unit"),      width: 80 },
    { field: "plannedValue",  headerName: t("program.goal.plannedValue"), width: 110 },
    { field: "plannedDate",   headerName: t("program.goal.plannedDate"),  width: 120,
      valueFormatter: (v) => formatDate(v) },
    { field: "deadline",      headerName: t("program.goal.deadline"),     width: 120,
      valueFormatter: (v) => formatDate(v) },
    { field: "actualValue",   headerName: t("program.achievement.actualValue"), width: 110 },
    { field: "actualDate",    headerName: t("program.achievement.actualDate"),  width: 120,
      valueFormatter: (v) => formatDate(v) },
    { field: "achievementCount", headerName: t("program.goalAchievement.count"), width: 90,
      type: "number" },
  ];

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6">{t("program.goalAchievement.list.title")}</Typography>
        {programName && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {programName}
          </Typography>
        )}
      </Box>
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(r) => r.id}
        loading={loading}
        autoHeight
        pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        disableRowSelectionOnClick
      />
      <Box sx={{ mt: 2 }}>
        <Button
          variant="outlined"
          onClick={() => navigate(`/build/${businessDomainId}/business-units/${businessUnitId}/programs/${programId}/members`)}
        >
          {t("common.back")}
        </Button>
      </Box>
    </Box>
  );
}
